import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileText, Boxes, Snowflake, ShieldCheck, BookOpen, ArrowRight, Search } from "lucide-react";
import { trackEvent } from "@/lib/track";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { Icon: FileText, title: "Specification Sheets", body: "Compound specifications — form, strength, CAS, sequence and molecular details — are published on each product page.", to: "/", cta: "Browse catalog" },
  { Icon: Boxes, title: "Batch Documentation", body: "Look up the documentation on record for a specific batch or lot number.", to: "/verify", cta: "Verify a batch" },
  { Icon: Snowflake, title: "Storage & Handling", body: "How materials are packaged and handled, with temperature-conscious care in transit.", to: "/standards", cta: "Our standards" },
  { Icon: ShieldCheck, title: "Quality Documentation", body: "Our sourcing, inventory integrity, and Research Use Only stance.", to: "/standards", cta: "View standards" },
  { Icon: BookOpen, title: "Research References", body: "A research reference library is being prepared. In the meantime, contact us for documentation on a specific compound.", to: "/contact", cta: "Contact us" },
];

export default function Documentation() {
  const [form, setForm] = useState({ email: "", sku: "", note: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/coa/request`, { email: form.email.trim().toLowerCase(), sku: form.sku.trim(), note: form.note.trim() });
      trackEvent("inquiry_submitted", { kind: "coa_request", sku: form.sku.trim() });
      toast.success("Request received — our team will follow up by email.");
      setForm({ email: "", sku: "", note: "" });
    } catch (err) { toast.error("Could not submit your request. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <main className="pt-32 pb-24" data-testid="documentation-page">
      <Seo title="Documentation Center — Specifications & Batch Documentation" path="/documentation"
        description="Access specification sheets, batch documentation, and quality information for Molecularix Biolabs research materials." />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Documentation</motion.p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.02] mb-5">Documentation Center</h1>
        <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-2xl mb-14">
          Specifications, batch documentation, and quality information for our reference-grade research materials. We publish what we have on file and never fabricate documentation.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mb-16">
          {SECTIONS.map(({ Icon, title, body, to, cta }) => (
            <div key={title} className="bg-[#0A0A0A] p-8 flex flex-col" data-testid={`doc-section-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <Icon size={20} className="text-[#D4AF37] mb-4" />
              <h2 className="font-serif text-xl font-light mb-2">{title}</h2>
              <p className="text-[#71717A] text-sm leading-relaxed flex-1">{body}</p>
              {to && (
                <Link to={to} className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] hover:underline">
                  {cta} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Request documentation */}
        <div className="border border-zinc-800 bg-[#0C0C0C] p-8 md:p-10" data-testid="coa-request">
          <h2 className="font-serif text-2xl md:text-3xl font-light mb-2">Request documentation</h2>
          <p className="text-[#71717A] text-sm leading-relaxed mb-7 max-w-xl">Can&apos;t find the specification or batch documentation you need? Tell us the compound or SKU and where to reach you, and our team will respond with the available documentation.</p>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="sm:col-span-1">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2" htmlFor="coa-email">Your email</label>
              <input id="coa-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="coa-email" placeholder="you@lab.edu"
                className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none px-4 py-3 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
            </div>
            <div className="sm:col-span-1 relative">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2" htmlFor="coa-sku">Compound or SKU</label>
              <Search size={15} className="absolute left-3.5 top-[42px] text-[#52525B]" aria-hidden="true" />
              <input id="coa-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                data-testid="coa-sku" placeholder="e.g. MX-RETATRUT-020"
                className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none pl-10 pr-4 py-3 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2" htmlFor="coa-note">Note (optional)</label>
              <textarea id="coa-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="coa-note" placeholder="Which documentation do you need?"
                className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm placeholder:text-[#52525B] transition-colors resize-none" />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={sending} data-testid="coa-submit"
                className="bg-[#D4AF37] text-black px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
                {sending ? "Sending…" : "Request documentation"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
